import { factoryURL } from "@/config/api-config";
import { rf } from "../../data/featureLayersDetails";
import { addFactoryStyles, addLightboxStyles } from "../../../../utils/mapUtilsStyles";
import { showLightbox, initLightbox, convertGoogleDriveUrl } from "../../../../utils/mapUtils";

const createTabsInterface = (feature) => {
  const tabContainer = document.createElement("div");

  // Create tab buttons
  const tabButtons = document.createElement("div");
  tabButtons.className = "factory-tabs";

  // Images tab
  const imagesTab = document.createElement("button");
  imagesTab.className = "factory-tab active";
  imagesTab.textContent = "Images";
  imagesTab.onclick = () => switchTab(tabContainer, "images");

  // Factory Details tab
  const factoryDetailsTab = document.createElement("button");
  factoryDetailsTab.className = "factory-tab";
  factoryDetailsTab.textContent = "Factory Details";
  factoryDetailsTab.onclick = () => switchTab(tabContainer, "factoryDetails");

  // Production Details tab
  const productionDetailsTab = document.createElement("button");
  productionDetailsTab.className = "factory-tab";
  productionDetailsTab.textContent = "Production Details";
  productionDetailsTab.onclick = () => switchTab(tabContainer, "productionDetails");

  tabButtons.appendChild(imagesTab);
  tabButtons.appendChild(factoryDetailsTab);
  tabButtons.appendChild(productionDetailsTab);
  tabContainer.appendChild(tabButtons);

  // Create tab content
  const createTabContent = (id) => {
    const content = document.createElement("div");
    content.id = `factory-tab-${id}`;
    content.className = "factory-tab-content";
    tabContainer.appendChild(content);
    return content;
  };

  // Create tab content containers
  const imagesContent = createTabContent("images");
  imagesContent.style.display = "block";
  const factoryDetailsContent = createTabContent("factoryDetails");
  const productionDetailsContent = createTabContent("productionDetails");

  // Populate tabs
  populateImagesTab(imagesContent, feature);
  populateFactoryDetailsTab(factoryDetailsContent, feature);
  populateProductionDetailsTab(productionDetailsContent, feature);

  return tabContainer;
};

const switchTab = (container, tabName) => {
  const tabContents = container.querySelectorAll(".factory-tab-content");
  tabContents.forEach((tab) => {
    tab.style.display = "none";
  });

  const tabs = container.querySelectorAll(".factory-tab");
  tabs.forEach((tab) => {
    tab.className = tab.className.replace(" active", "");
  });

  document.getElementById(`factory-tab-${tabName}`).style.display = "block";

  // Map tabName to tab index (1-based for nth-child)
  const tabIndex = {
    images: 1,
    factoryDetails: 2,
    productionDetails: 3,
  };
  const activeTab = container.querySelector(
    `.factory-tab:nth-child(${tabIndex[tabName]})`
  );
  activeTab.className += " active";
};

const populateImagesTab = (container, feature) => {
  // Parse image URLs
  let imageUrls = feature.graphic.attributes.imageUrls;
  if (typeof imageUrls === "string" && imageUrls) {
    try {
      imageUrls = JSON.parse(imageUrls);
    } catch (error) {
      console.warn("imageUrls is not valid JSON, splitting by comma:", imageUrls);
      imageUrls = imageUrls
        .split(",")
        .map((url) => url.trim())
        .filter((url) => url);
    }
  }

  if (!Array.isArray(imageUrls)) {
    imageUrls = [];
  }

  // Initialize lightbox and styles
  initLightbox("lightbox");
  addLightboxStyles();

  // Create gallery container
  const gallery = document.createElement("div");
  gallery.className = "factory-gallery";

  if (imageUrls.length === 0) {
    const noImagesMsg = document.createElement("p");
    noImagesMsg.textContent = "No images available";
    noImagesMsg.style.margin = "10px 0";
    container.appendChild(noImagesMsg);
    return;
  }

  // Convert all URLs upfront
  const convertedUrls = imageUrls.map(url => convertGoogleDriveUrl(url));

  // Create gallery items
  imageUrls.forEach((url, index) => {
    const convertedUrl = convertGoogleDriveUrl(url);
    const galleryItem = document.createElement("div");
    galleryItem.className = "factory-gallery-item";

    // Loader container
    const loaderContainer = document.createElement("div");
    loaderContainer.className = "factory-loader-container";
    const loader = document.createElement("div");
    loader.className = "factory-loader";
    loaderContainer.appendChild(loader);

    // Image element
    const img = document.createElement("img");
    img.style.display = "none";
    img.alt = `Factory Image ${index + 1}`;
    // img.loading = "lazy";

    img.onload = () => {
      console.log(`Successfully loaded image: ${convertedUrl}`);
      loaderContainer.style.display = "none";
      img.style.display = "block";
    };

    img.onerror = () => {
      console.error(`Failed to load image: ${convertedUrl}`);

      if (convertedUrl.includes("drive.google.com/thumbnail")) {
        const fileId = new URL(convertedUrl).searchParams.get("id");
        if (fileId) {
          const fallbackUrl = `https://drive.google.com/uc?export=view&id=${fileId}`;
          img.src = fallbackUrl;

          img.onerror = () => {
            console.error(`Both image loading methods failed for ID: ${fileId}`);
            loaderContainer.innerHTML = "";
            const errorMsg = document.createElement("p");
            errorMsg.textContent = `Image ${index + 1} failed to load`;
            errorMsg.style.color = "red";
            errorMsg.style.fontSize = "12px";
            errorMsg.style.textAlign = "center";
            loaderContainer.appendChild(errorMsg);
          };
          return;
        }
      }

      loaderContainer.innerHTML = "";
      const errorMsg = document.createElement("p");
      errorMsg.textContent = `Image ${index + 1} failed to load`;
      errorMsg.style.color = "red";
      errorMsg.style.fontSize = "12px";
      errorMsg.style.textAlign = "center";
      loaderContainer.appendChild(errorMsg);
    };

    img.src = convertedUrl;
    
    // Updated click handler using reusable lightbox
    img.onclick = () => showLightbox(convertedUrls, index, "lightbox");

    galleryItem.appendChild(loaderContainer);
    galleryItem.appendChild(img);
    gallery.appendChild(galleryItem);
  });

  container.appendChild(gallery);
};

const populateFactoryDetailsTab = (container, feature) => {
  const table = document.createElement("table");
  table.className = "factory-fields-table";

  const fieldInfos = [
    { fieldName: "name", label: "Name" },
    { fieldName: "type", label: "Type" },
    {
      fieldName: "materials",
      label: "Materials",
      getValue: (attributes) => {
        if (attributes.type === "Biochar Facility" && attributes.biocharMaterials) {
          const materials = typeof attributes.biocharMaterials === "string"
            ? attributes.biocharMaterials
            : attributes.biocharMaterials.join(", ");
          return materials;
        }
        return attributes.rawMaterials || "N/A";
      },
    },
    { fieldName: "status", label: "Status" },
    { fieldName: "markerType", label: "Marker Type" },
    { fieldName: "latitude", label: "Latitude" },
    { fieldName: "longitude", label: "Longitude" },
  ];

  fieldInfos.forEach((field) => {
    const value = field.getValue
      ? field.getValue(feature.graphic.attributes)
      : feature.graphic.attributes[field.fieldName] || "N/A";

    if (value !== "N/A" || field.fieldName === "name" || field.fieldName === "type" || field.fieldName === "status") {
      const row = document.createElement("tr");

      const labelCell = document.createElement("td");
      labelCell.className = "factory-field-label";
      labelCell.textContent = field.label;

      const valueCell = document.createElement("td");
      valueCell.textContent = value;

      row.appendChild(labelCell);
      row.appendChild(valueCell);
      table.appendChild(row);
    }
  });

  container.appendChild(table);
};

const populateProductionDetailsTab = (container, feature) => {
    const table = document.createElement("table");
    table.className = "factory-fields-table";
  
    const isBiocharFacility = feature.graphic.attributes.type === "Biochar Facility";
  
    // Define fields conditionally
    const fieldInfos = [
      ...(isBiocharFacility
        ? [
            { 
              fieldName: "processedVolume", 
              label: "Processed Volume (tons/day)" 
            },
          ]
        : []),
      { 
        fieldName: "inventoryLevels", 
        label: "Inventory Levels" 
      },
    ];
  
    fieldInfos.forEach((field) => {
      const value = feature.graphic.attributes[field.fieldName] ?? "N/A";
  
      const row = document.createElement("tr");
  
      const labelCell = document.createElement("td");
      labelCell.className = "factory-field-label";
      labelCell.textContent = field.label;
  
      const valueCell = document.createElement("td");
      valueCell.textContent = value;
  
      row.appendChild(labelCell);
      row.appendChild(valueCell);
      table.appendChild(row);
    });
  
    container.appendChild(table);
  };

const releafFactories = {
  url: factoryURL,
  title: "Releaf Assets",
  renderer: rf.renderer,
  outFields: ["*"],
  fields: [
    { name: "id", type: "string" },
    { name: "name", type: "string" },
    { name: "type", type: "string" },
    { name: "rawMaterials", type: "string" },
    { name: "biocharMaterials", type: "string" },
    { name: "inputRate", type: "string" },
    { name: "outputRate", type: "string" },
    { name: "markerType", type: "string" },
    { name: "status", type: "string" },
    { name: "latitude", type: "double" },
    { name: "longitude", type: "double" },
    { name: "imageUrls", type: "string" },
    { name: "productionCapacity", type: "double" },
    { name: "processedVolume", type: "double" },
    { name: "inventoryLevels", type: "double" },
  ],
  popupTemplate: {
    title: "Releaf Assets - {name}",
    content: [
      {
        type: "custom",
        creator: function (feature) {
          addFactoryStyles();
          return createTabsInterface(feature);
        },
      },
    ],
  },
};

export default releafFactories;